import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { EnvironmentProviders, inject, makeEnvironmentProviders, NgModule, provideEnvironmentInitializer } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { BrowserModule, DomSanitizer, Title } from '@angular/platform-browser';
import { FullCalendarModule } from '@fullcalendar/angular';
import { AuthPageActions, provideAuthModule } from '@iot-platform/auth';
import {
  ApiInterceptorModule,
  GoogleDriveService,
  LocalStorageModule,
  NavigationLoggerService,
  provideDefaultProvidersToken,
  provideIotHighcharts,
  provideIotNativeDateAdapter
} from '@iot-platform/core';
import { provideDeviceCommandSettings, provideDeviceConnectivityModuleSettings } from '@iot-platform/data-access/devices';
import { FavoriteViewsModule } from '@iot-platform/data-access/favorite-views';
import { PreferencesModule, ProfileModule, UserPreferencesFavoriteFilterEngineService } from '@iot-platform/data-access/users';
import { I18nModule } from '@iot-platform/i18n';
import { FilterEngineV2Module } from '@iot-platform/iot-platform-ui';
import {
  DeviceCommandService as I4bDeviceCommandService,
  DeviceConnectivityModuleService as I4bDeviceConnectivityModuleService
} from '@iot-platform/iot4bos/data-access/devices';
import { I4bAuditTrailModule, provideI4bTrackingProviders } from '@iot-platform/iot4bos/util';
import { AppName } from '@iot-platform/models/common';
import { NotificationModule } from '@iot-platform/notification';
import { EffectsModule } from '@ngrx/effects';
import { DEFAULT_ROUTER_FEATURENAME, FullRouterStateSerializer, NavigationActionTiming, provideRouterStore, routerReducer } from '@ngrx/router-store';
import { ActionReducer, MetaReducer, provideStore, StoreModule } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { LicenseManager as AgGridLicenseManager } from 'ag-grid-enterprise';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { AppRouterModule } from './app.router.module';

export function clearState(reducer: ActionReducer<unknown>): ActionReducer<unknown> {
  return function (state, action) {
    if (action.type === AuthPageActions.signOut.type) {
      state = undefined;
    }
    return reducer(state, action);
  };
}

export const metaReducers: MetaReducer<unknown>[] = [clearState];

const storeSettings = {
  metaReducers,
  runtimeChecks: {
    strictStateImmutability: false,
    strictActionImmutability: false,
    strictStateSerializability: false,
    strictActionSerializability: false
  }
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    CommonModule,
    LocalStorageModule.forRoot({ prefix: environment.storage.appPrefix }),
    AppRouterModule,
    StoreModule.forRoot({}, storeSettings),
    EffectsModule.forRoot([]),
    PreferencesModule,
    ProfileModule,
    I18nModule.forRoot({ langs: ['fr', 'en', 'de', 'es', 'it'] }),
    NotificationModule.forRoot({ disableErrorMessagesAfterLogout: true }),
    FullCalendarModule,
    ApiInterceptorModule.forRoot({
      includeAppVersionInHeader: true,
      excludedApis: ['/v2undefined', '/entities/undefined/admin-profiles'],
      replaceApis: [
        {
          endpoint: `${environment.api.url}/po-event-rules`,
          replaceBy: `${environment.api.url}${environment.api.endpoints.poEventRules}`
        }
      ]
    }),
    I4bAuditTrailModule,
    FavoriteViewsModule,
    FilterEngineV2Module.withSettings({
      userPreferencesService: UserPreferencesFavoriteFilterEngineService
    })
  ],
  providers: [
    provideApplicationInitializer(),
    provideIotHighcharts(),
    provideStore(
      {
        [DEFAULT_ROUTER_FEATURENAME]: routerReducer
      },
      storeSettings
    ),
    provideStoreDevtools({ maxAge: 30, logOnly: environment.production }),
    provideRouterStore({
      serializer: FullRouterStateSerializer,
      navigationActionTiming: NavigationActionTiming.PostActivation
    }),
    provideAuthModule({
      appName: AppName.IOT4BOS
    }),
    provideDefaultProvidersToken({ environment }),
    provideI4bTrackingProviders(),
    provideIotNativeDateAdapter(),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideDeviceCommandSettings({ deviceCommandService: I4bDeviceCommandService }),
    provideDeviceConnectivityModuleSettings({ deviceConnectivityModuleService: I4bDeviceConnectivityModuleService })
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}

function provideApplicationInitializer(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideEnvironmentInitializer(() => {
      inject(GoogleDriveService).loadGoogleLibraries();
      inject(NavigationLoggerService);
      inject(MatIconRegistry).addSvgIconSet(inject(DomSanitizer).bypassSecurityTrustResourceUrl('assets/svg/iot4bos-icon-set.svg'));
      AgGridLicenseManager.setLicenseKey(environment.apiKeys.agGrid);
      if (environment.envType !== 'prod') {
        const link: HTMLLinkElement | null = document.querySelector('link[rel="icon"]');
        if (link) {
          link.href = `/assets/favicons/favicon-${environment.envType}.ico`;
        }
        const titleService = inject(Title);
        titleService.setTitle(`${titleService.getTitle()} - ${environment.envName}`);
      }
    })
  ]);
}
